import { Button, Spinner } from 'src/components/atoms';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import {
  DynamicQuestion,
  type DynamicQuestionAnswerValue,
} from 'src/components/molecules';
import { addingFilesAsEvidenceIntercomArticle } from 'src/utils/intercomArticles';
import type { GetQuestionnaireQuestionsQuery } from 'src/graphql/generated';
import {
  QuestionsContainers,
  QuestionsList,
  SectionError,
  SubmitReportButtonContainer,
} from './ManualIntakeDynamicQuestions.styles';
import { mapAnswersToSaveInput } from './mapAnswersToSaveInput';
import type {
  AnswersState,
  SubmitReportPayload,
} from './manualIntakeDynamicQuestions.types';

type QuestionnaireQuestion = NonNullable<
  NonNullable<
    GetQuestionnaireQuestionsQuery['questionnaireQuestions']
  >['questions']
>[number];

type ManualIntakeQuestionsFormProps = {
  questions: QuestionnaireQuestion[];
  answers: AnswersState;
  setAnswer: (questionId: string, value: DynamicQuestionAnswerValue) => void;
  submitAttempted: boolean;
  setSubmitAttempted: (value: boolean) => void;
  allRequiredQuestionsAnswered: boolean;
  isSubmittingReport: boolean;
  canSubmitReport: boolean;
  selectedQuestionnaireId: string | null;
  responseId: string | null;
  evidenceUploadReportId?: string | null;
  canSyncAttachmentsToQa: boolean;
  onRegisterQuestionnaireAttachment: (input: {
    questionId: string;
    evidenceS3Path: string;
  }) => Promise<string | undefined>;
  onDeleteQuestionnaireAttachment: (
    input: { attachmentId: string; questionId: string },
    opts?: { onError?: (error: unknown) => void },
  ) => Promise<void>;
  onSubmitReport?: (payload: SubmitReportPayload) => void;
  onSubmitReportFromReview?: (payload: SubmitReportPayload) => void;
};

export function ManualIntakeQuestionsForm({
  questions,
  answers,
  setAnswer,
  submitAttempted,
  setSubmitAttempted,
  allRequiredQuestionsAnswered,
  isSubmittingReport,
  canSubmitReport,
  selectedQuestionnaireId,
  responseId,
  evidenceUploadReportId,
  canSyncAttachmentsToQa,
  onRegisterQuestionnaireAttachment,
  onDeleteQuestionnaireAttachment,
  onSubmitReport,
  onSubmitReportFromReview,
}: ManualIntakeQuestionsFormProps) {
  return (
    <QuestionsContainers>
      <QuestionsList>
        {questions.map((question) => {
          const questionId = question?.id == null ? '' : String(question.id);
          const questionText = question?.questionText?.trim() ?? '';
          const answerValue =
            answers[questionId] ??
            (questionText
              ? answers[`questionText:${questionText}`]
              : undefined) ??
            {};
          return (
            <DynamicQuestion
              key={question?.id ?? question?.orderIndex}
              question={question ?? {}}
              value={answerValue}
              onChange={(value) => questionId && setAnswer(questionId, value)}
              fileUploadHelpArticleUrl={addingFilesAsEvidenceIntercomArticle}
              reportIdForEvidenceUpload={evidenceUploadReportId}
              showValidation={submitAttempted}
              onRegisterQuestionnaireAttachment={
                canSyncAttachmentsToQa
                  ? onRegisterQuestionnaireAttachment
                  : undefined
              }
              onDeleteQuestionnaireAttachment={
                canSyncAttachmentsToQa
                  ? onDeleteQuestionnaireAttachment
                  : undefined
              }
              data-testid={`manual-intake-question-${question?.id}`}
            />
          );
        })}
        <SubmitReportButtonContainer>
          <Button
            variant="primary"
            size="medium"
            loading={isSubmittingReport}
            onClick={() => {
              if (!selectedQuestionnaireId) return;
              if (!allRequiredQuestionsAnswered) {
                setSubmitAttempted(true);
                return;
              }
              const payload: SubmitReportPayload = {
                questionnaireId: selectedQuestionnaireId,
                responseId: responseId ?? undefined,
                answers: mapAnswersToSaveInput(answers),
                attestationAccepted: true,
              };
              if (onSubmitReportFromReview) {
                onSubmitReportFromReview(payload);
                return;
              }
              onSubmitReport?.(payload);
            }}
            disabled={isSubmittingReport || !canSubmitReport}
            data-testid="manual-intake-submit-report"
          >
            Submit report
            {isSubmittingReport ? (
              <Spinner
                size="large"
                variant="primary"
                data-testid="manual-intake-submit-report-spinner"
              />
            ) : (
              <StyledIconInButton
                size="medium"
                position="right"
                className="uil uil-angle-right"
              />
            )}
          </Button>
          {submitAttempted && !allRequiredQuestionsAnswered && (
            <SectionError data-testid="manual-intake-required-missing">
              Please answer all required questions to submit your report.
            </SectionError>
          )}
        </SubmitReportButtonContainer>
      </QuestionsList>
    </QuestionsContainers>
  );
}
